/**
 * `renderView` — one function, six layouts, zero tenant-specific code.
 *
 * A View names a layout and binds that layout's slots to fields on the rows a
 * capability returns. Everything tenant-shaped therefore lives in the manifest,
 * and this file only has to know what a "card" is. That is the whole reason both
 * surfaces can share ~85% of their rendering.
 *
 * Slot vocabulary (all optional; unknown slot names still render):
 *   title     primary label, and the thing `linkField` wraps
 *   subtitle  secondary line
 *   meta      tertiary, dimmed line
 *   time      leading value in `timeline`, emitted as a real <time>
 *   venue     trailing qualifier under a timeline entry
 *   cta       label for the outbound link button
 *   note      free text under the card
 * Any other slot becomes a labelled `key: value` pair (a <dl> row in `detail`,
 * a column in `table`, part of the meta line elsewhere).
 */
import { html } from 'htm/preact';
import type { VNode } from 'preact';
import type { View } from '@clawharbor/manifest';
import type { RenderOpts, Row } from './types.js';
import { renderContrastNotice, renderHeader } from './chrome.js';
import {
  asRows,
  badgesFor,
  datetimeAttr,
  groupBy,
  humanize,
  lastSegment,
  resolveSlot,
  safeHref,
  slotText,
  stringify,
  type Badge,
} from './slots.js';

/** Slots the layouts consume by name; everything else falls through to `extras`. */
const RESERVED = new Set(['title', 'subtitle', 'meta', 'time', 'venue', 'cta', 'note']);

const UNAVAILABLE = /^(sold.?out|disabled|unavailable|cancell?ed|soldOut)$/i;

interface Entry {
  row: Row;
  /** Index into the ORIGINAL rows array, which is what `onSelect` must report. */
  index: number;
}

/** Third-party rows carry availability under half a dozen spellings. */
function isUnavailable(row: Row): boolean {
  for (const [k, v] of Object.entries(row)) {
    if (UNAVAILABLE.test(k) && v === true) return true;
  }
  return false;
}

function extraSlots(view: View): Array<[string, string]> {
  return Object.entries(view.slots ?? {}).filter(([name]) => !RESERVED.has(name));
}

function renderBadges(badges: Badge[]): VNode | null {
  if (!badges.length) return null;
  return html`
    <ul class="ch-badges">
      ${badges.map(
        (b, i) => html`
          <li key=${`${b.field}-${i}`} class="ch-badge ch-badge--${b.tone}">
            <span class="ch-sr">${humanize(lastSegment(b.field))}: </span>${b.label}
          </li>
        `,
      )}
    </ul>
  `;
}

/**
 * A link is rendered as an anchor and a stage action as a button — never a
 * clickable div. `deepLinkOnly` means the anchor is the entire transaction
 * story, so it has to be a real, keyboard-reachable, middle-clickable link.
 */
function renderPrimary(label: string, href: string | null, className?: string): VNode {
  const cls = className || undefined;
  if (!href) return html`<span class=${cls}>${label}</span>`;
  return html`<a class=${cls} href=${href} target="_blank" rel="noopener noreferrer">${label}</a>`;
}

function renderCard(view: View, entry: Entry, opts: RenderOpts): VNode {
  const { row, index } = entry;
  const labels = opts.labels;
  const slots = view.slots ?? {};

  const title = slotText(slots['title'], row, labels) ?? firstValue(row) ?? '—';
  const subtitle = slotText(slots['subtitle'], row, labels);
  const meta = slotText(slots['meta'], row, labels);
  const note = slotText(slots['note'], row, labels);
  const href = safeHref(resolveSlot(view.linkField, row), opts.origin);
  const badges = badgesFor(view.badges ?? [], row, labels);
  const disabled = isUnavailable(row);

  const extras = extraSlots(view)
    .map(([name, spec]) => {
      const v = slotText(spec, row, labels);
      return v ? `${humanize(name)}: ${v}` : null;
    })
    .filter((s): s is string => s != null);

  const ctaLabel = slotText(slots['cta'], row, labels) ?? 'Open';

  return html`
    <li key=${index} class=${`ch-card${disabled ? ' is-disabled' : ''}`}>
      <p class="ch-card__title">${renderPrimary(title, href)}</p>
      ${subtitle ? html`<p class="ch-card__sub">${subtitle}</p>` : null}
      ${renderBadges(badges)}
      ${meta ? html`<p class="ch-card__meta">${meta}</p>` : null}
      ${extras.length ? html`<p class="ch-card__meta">${extras.join(' · ')}</p>` : null}
      ${note ? html`<p class="ch-card__meta">${note}</p>` : null}
      ${opts.onSelect || (href && slots['cta'])
        ? html`
            <div class="ch-card__actions">
              ${href && slots['cta']
                ? html`<a class="ch-btn" href=${href} target="_blank" rel="noopener noreferrer">${ctaLabel}</a>`
                : null}
              ${opts.onSelect
                ? html`
                    <button
                      type="button"
                      class="ch-btn ch-btn--ghost"
                      aria-disabled=${disabled ? 'true' : 'false'}
                      onClick=${() => opts.onSelect?.(row, index)}
                    >
                      ${opts.selectLabel ?? 'Select'}<span class="ch-sr"> ${title}</span>
                    </button>
                  `
                : null}
            </div>
          `
        : null}
    </li>
  `;
}

function firstValue(row: Row): string | undefined {
  for (const v of Object.values(row)) {
    const s = stringify(v);
    if (s) return s;
  }
  return undefined;
}

function renderCards(view: View, entries: Entry[], opts: RenderOpts, kind: 'list' | 'grid'): VNode {
  return html`<ul class=${`ch-${kind}`}>${entries.map((e) => renderCard(view, e, opts))}</ul>`;
}

/** Timeline entries are compact by design: a time, a qualifier, and format chips. */
function renderTimeline(view: View, entries: Entry[], opts: RenderOpts): VNode {
  const slots = view.slots ?? {};
  return html`
    <ol class="ch-timeline">
      ${entries.map(({ row, index }) => {
        const time = slotText(slots['time'], row, opts.labels) ?? slotText(slots['title'], row, opts.labels) ?? '—';
        const venue = slotText(slots['venue'], row, opts.labels) ?? slotText(slots['subtitle'], row, opts.labels);
        const badges = badgesFor(view.badges ?? [], row, opts.labels);
        const href = safeHref(resolveSlot(view.linkField, row), opts.origin);
        const disabled = isUnavailable(row);
        const dt = datetimeAttr(time);
        const cls = `ch-perf${disabled ? ' is-disabled' : ''}`;
        const label = `${time}${venue ? `, ${venue}` : ''}${disabled ? ' — sold out' : ''}`;

        const inner = html`
          ${dt
            ? html`<time class="ch-slot--time" datetime=${dt}>${time}</time>`
            : html`<span class="ch-slot--time">${time}</span>`}
          ${venue ? html`<span class="ch-card__meta">${venue}</span>` : null}
          ${renderBadges(badges)}
        `;

        return html`
          <li key=${index}>
            ${href
              ? html`<a class=${cls} href=${href} target="_blank" rel="noopener noreferrer" aria-label=${label}
                  >${inner}</a
                >`
              : opts.onSelect
                ? html`<button
                    type="button"
                    class=${cls}
                    aria-label=${label}
                    aria-disabled=${disabled ? 'true' : 'false'}
                    onClick=${() => opts.onSelect?.(row, index)}
                  >
                    ${inner}
                  </button>`
                : html`<span class=${cls}>${inner}</span>`}
          </li>
        `;
      })}
    </ol>
  `;
}

/**
 * Columns come from `view.slots` in declaration order. When a view declares none
 * we fall back to the union of the first rows' own keys, so a table view is still
 * useful before anyone has written its slots.
 */
function tableColumns(view: View, entries: Entry[]): Array<[string, string]> {
  const declared = Object.entries(view.slots ?? {});
  if (declared.length) return declared;
  const keys: string[] = [];
  for (const { row } of entries.slice(0, 5)) {
    for (const k of Object.keys(row)) if (!keys.includes(k)) keys.push(k);
  }
  return keys.map((k) => [humanize(k), k]);
}

function renderTable(view: View, entries: Entry[], opts: RenderOpts, caption?: string): VNode {
  const cols = tableColumns(view, entries);
  return html`
    <div class="ch-table-scroll">
      <table class="ch-table">
        ${caption ? html`<caption>${caption}</caption>` : null}
        <thead>
          <tr>
            ${cols.map(([name]) => html`<th key=${name} scope="col">${humanize(name)}</th>`)}
          </tr>
        </thead>
        <tbody>
          ${entries.map(({ row, index }) => {
            const href = safeHref(resolveSlot(view.linkField, row), opts.origin);
            return html`
              <tr key=${index} class=${isUnavailable(row) ? 'is-disabled' : undefined}>
                ${cols.map(([name, spec], ci) => {
                  const value = slotText(spec, row, opts.labels) ?? '';
                  const content = ci === 0 ? renderPrimary(value || '—', href) : value;
                  return ci === 0
                    ? html`<th key=${name} scope="row">${content}</th>`
                    : html`<td key=${name}>${content}</td>`;
                })}
              </tr>
            `;
          })}
        </tbody>
      </table>
    </div>
  `;
}

function renderDetail(view: View, entries: Entry[], opts: RenderOpts): VNode {
  const first = entries[0];
  if (!first) return renderEmptyBody(view, opts);
  const { row, index } = first;
  const slots = view.slots ?? {};

  const title = slotText(slots['title'], row, opts.labels) ?? firstValue(row) ?? '—';
  const subtitle = slotText(slots['subtitle'], row, opts.labels);
  const href = safeHref(resolveSlot(view.linkField, row), opts.origin);
  const badges = badgesFor(view.badges ?? [], row, opts.labels);
  // Detail is the only layout that shows everything: the reserved slots that the
  // other layouts fold into a meta line become first-class <dl> rows here.
  const candidates: Array<[string, string | undefined]> = [
    ['time', slots['time']],
    ['venue', slots['venue']],
    ['meta', slots['meta']],
    ['note', slots['note']],
    ...extraSlots(view),
  ];
  const pairs = candidates
    .map(([name, spec]) => [name, slotText(spec, row, opts.labels)] as const)
    .filter((p): p is readonly [string, string] => Boolean(p[1]));

  return html`
    <article class="ch-detail">
      <h3 class="ch-card__title">${title}</h3>
      ${subtitle ? html`<p class="ch-card__sub">${subtitle}</p>` : null}
      ${renderBadges(badges)}
      ${pairs.length
        ? html`
            <dl class="ch-dl">
              ${pairs.map(
                ([name, value]) => html`
                  <dt key=${`t-${name}`}>${humanize(name)}</dt>
                  <dd key=${`d-${name}`}>${value}</dd>
                `,
              )}
            </dl>
          `
        : null}
      ${href || opts.onSelect
        ? html`
            <div class="ch-card__actions">
              ${href
                ? html`<a class="ch-btn" href=${href} target="_blank" rel="noopener noreferrer"
                    >${slotText(slots['cta'], row, opts.labels) ?? 'Open on the official site'}</a
                  >`
                : null}
              ${opts.onSelect
                ? html`<button type="button" class="ch-btn ch-btn--ghost" onClick=${() => opts.onSelect?.(row, index)}>
                    ${opts.selectLabel ?? 'Select'}
                  </button>`
                : null}
            </div>
          `
        : null}
    </article>
  `;
}

function renderEmptyBody(view: View, opts: RenderOpts): VNode {
  const text = opts.emptyText ?? view.emptyText ?? 'Nothing to show.';
  return html`<p class="ch-empty" role="status">${text}</p>`;
}

/** Wrap the body in one `<section>` per group, or render it bare when ungrouped. */
function withGroups(
  view: View,
  entries: Entry[],
  opts: RenderOpts,
  body: (entries: Entry[], groupKey?: string) => VNode,
): VNode {
  const groups = groupBy(entries, view.groupBy, (e) => e.row);
  if (!view.groupBy || groups.length <= 1) {
    const only = groups[0];
    return body(only ? only.items : entries, only?.key || undefined);
  }
  const level = (opts.headingLevel ?? 2) + 1;
  const Tag = `h${Math.min(level, 6)}`;
  return html`
    <div class="ch-groups">
      ${groups.map(
        (g) => html`
          <section class="ch-group" key=${g.key}>
            <${Tag} class="ch-group__title">${g.key}<//>
            ${body(g.items, g.key)}
          </section>
        `,
      )}
    </div>
  `;
}

/**
 * Render a view's rows.
 *
 * `rows` is `unknown[]` because it arrives straight off a binding pipeline that
 * scraped someone else's HTML. Non-object rows are wrapped rather than dropped —
 * showing a bare string beats showing nothing while a binding is being tuned.
 */
export function renderView(view: View, rows: unknown[], opts: RenderOpts = {}): VNode {
  const all = asRows(rows ?? []);
  const capped = opts.maxRows != null && opts.maxRows >= 0 ? all.slice(0, opts.maxRows) : all;
  const entries: Entry[] = capped.map((row, index) => ({ row, index }));

  const body =
    view.layout === 'empty' || entries.length === 0
      ? renderEmptyBody(view, opts)
      : view.layout === 'table'
        ? withGroups(view, entries, opts, (es, key) => renderTable(view, es, opts, key))
        : view.layout === 'timeline'
          ? withGroups(view, entries, opts, (es) => renderTimeline(view, es, opts))
          : view.layout === 'detail' || view.layout === 'form'
            ? renderDetail(view, entries, opts)
            : withGroups(view, entries, opts, (es) => renderCards(view, es, opts, view.layout as 'list' | 'grid'));

  const truncated = all.length > entries.length;
  const header =
    opts.showHeader === false
      ? null
      : renderHeader(opts.title ?? view.title, {
          freshness: opts.freshness,
          level: opts.headingLevel ?? 2,
          now: opts.now,
        });

  return html`
    <section class=${`ch-root ch-view ch-view--${view.layout}${opts.className ? ` ${opts.className}` : ''}`} data-view=${view.id}>
      ${header}
      ${opts.showContrastWarnings ? renderContrastNotice(opts.brand) : null}
      ${body}
      ${truncated
        ? html`<p class="ch-card__meta">Showing ${entries.length} of ${all.length}. Open the official site for the rest.</p>`
        : null}
    </section>
  `;
}

/** Standalone empty-state renderer, for surfaces that need it outside a view. */
export function renderEmpty(text: string): VNode {
  return html`<p class="ch-empty" role="status">${text}</p>`;
}
